<template>
    <div class="weeduc-admin-page course-group-detail text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="group?.name || $t('admin.courseGroupDetail.titleFallback')"
                    :subtitle="group?.description || $t('admin.courseGroupDetail.subtitleFallback')"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn variant="text" class="text-none" @click="goBack">
                    Voltar
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="editGroup"
                >
                    <v-icon class="me-1" size="small">mdi-pencil</v-icon>
                    Editar grupo
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="loading" class="course-group-detail__placeholder">
                <Loader :isActive="false" />
            </div>
            <div v-else-if="error" class="course-group-detail__placeholder">
                Não foi possível carregar o grupo.
            </div>
            <template v-else>
                <section
                    class="course-group-detail__hero"
                    :class="{ 'has-image': !!coverUrl }"
                    :style="heroStyle"
                >
                    <div class="course-group-detail__hero-overlay">
                        <div class="course-group-detail__chips">
                            <span
                                class="course-group-detail__chip"
                                :class="isPath ? 'is-path' : 'is-group'"
                            >
                                {{ isPath ? $t('admin.courses.path') : $t('admin.courseGroupDetail.free') }}
                            </span>
                            <span
                                class="course-group-detail__chip"
                                :class="statusChipClass"
                            >
                                {{ statusChipLabel }}
                            </span>
                        </div>
                        <h2 class="course-group-detail__hero-title">{{ group.name }}</h2>
                        <p v-if="groupDescription" class="course-group-detail__hero-desc">
                            {{ groupDescription }}
                        </p>
                        <p class="course-group-detail__hero-meta">
                            {{ sortableRows.length }}
                            {{ sortableRows.length === 1 ? $t('admin.courseGroupDetail.courseSingular') : $t('admin.courseGroupDetail.coursePlural') }}
                        </p>
                    </div>
                </section>

                <div class="weeduc-admin-page__panel course-group-detail__panel">
                    <header class="course-group-detail__section-head">
                        <div>
                            <h3>Cursos do grupo</h3>
                            <p>
                                Conteúdos que fazem parte deste
                                {{ isPath ? 'trilha' : 'grupo' }}.
                            </p>
                        </div>
                        <div class="course-group-detail__section-actions">
                            <v-select
                                :model-value="groupMode"
                                :items="modeOptions"
                                item-title="text"
                                item-value="value"
                                :label="$t('admin.courseGroupDetail.modeLabel')"
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="course-group-detail__mode-select weeduc-admin-v-field"
                                :disabled="saving"
                                @update:model-value="onModeChange"
                            />
                            <v-btn
                                color="primary"
                                variant="tonal"
                                rounded="lg"
                                class="text-none"
                                :disabled="!availableCourses.length || saving"
                                @click="openAddDialog"
                            >
                                <v-icon class="me-1" size="small">mdi-plus</v-icon>
                                Adicionar curso
                            </v-btn>
                        </div>
                    </header>

                    <div v-if="!sortableRows.length" class="course-group-detail__empty">
                        Nenhum curso neste grupo. Adicione um curso existente para começar.
                    </div>
                    <draggable
                        v-else
                        v-model="sortableRows"
                        item-key="courseId"
                        handle=".course-group-detail__drag-handle"
                        ghost-class="course-group-detail__ghost"
                        class="course-group-detail__list"
                        :disabled="saving"
                        @end="onDragEnd"
                    >
                        <template #item="{ element: row, index }">
                            <article class="course-group-detail__row">
                                <div class="course-group-detail__row-main">
                                    <button
                                        type="button"
                                        class="course-group-detail__drag-handle"
                                        title="Arrastar para reordenar"
                                        aria-label="Arrastar para reordenar"
                                        :disabled="saving"
                                    >
                                        <v-icon size="small">mdi-drag-vertical</v-icon>
                                    </button>
                                    <span class="course-group-detail__order">{{ index + 1 }}</span>
                                    <button
                                        type="button"
                                        class="course-group-detail__thumb"
                                        :class="{ 'no-image': !row.imageUrl }"
                                        :style="thumbStyle(row)"
                                        :aria-label="`Ver ${row.courseName}`"
                                        @click="viewCourse(row.courseId)"
                                    />
                                    <div class="course-group-detail__meta">
                                        <button
                                            type="button"
                                            class="course-group-detail__title-btn"
                                            @click="viewCourse(row.courseId)"
                                        >
                                            <span class="course-group-detail__title">{{ row.courseName }}</span>
                                        </button>
                                        <p
                                            v-if="row.description"
                                            class="course-group-detail__desc"
                                        >
                                            {{ row.description }}
                                        </p>
                                        <div class="course-group-detail__stats">
                                            <span
                                                class="course-group-detail__status"
                                                :class="row.active ? 'is-active' : 'is-inactive'"
                                            >
                                                {{ row.active ? 'Ativo' : 'Inativo' }}
                                            </span>
                                            <span>{{ row.moduleQty }} módulos</span>
                                            <span aria-hidden="true">·</span>
                                            <span>{{ row.lessonQty }} aulas</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="course-group-detail__toolbar" @click.stop>
                                    <v-btn
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        rounded="lg"
                                        class="text-none"
                                        :disabled="!row.courseId"
                                        @click="viewCourse(row.courseId)"
                                    >
                                        Ver curso
                                    </v-btn>
                                </div>
                            </article>
                        </template>
                    </draggable>
                </div>
            </template>
        </div>

        <v-dialog
            v-model="addDialog"
            max-width="920"
            scrollable
            scrim="rgba(15, 20, 40, 0.45)"
        >
            <div class="weeduc-admin-page__panel course-group-detail__dialog">
                <header class="course-group-detail__dialog-head">
                    <div>
                        <h3>Adicionar curso</h3>
                        <p>Escolha um ou mais cursos da plataforma para incluir neste grupo.</p>
                    </div>
                    <v-text-field
                        v-model="courseSearch"
                        :label="$t('admin.courseGroupDetail.searchByTitle')"
                        placeholder="Digite o nome do curso"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        class="weeduc-admin-v-field course-group-detail__dialog-search"
                    />
                </header>

                <div v-if="!filteredAvailableCourses.length" class="course-group-detail__dialog-empty">
                    Nenhum curso disponível para adicionar.
                </div>
                <div
                    v-else
                    ref="pickerScroll"
                    class="course-group-detail__picker-grid"
                    @scroll.passive="onPickerScroll"
                >
                    <button
                        v-for="course in visiblePickerCourses"
                        :key="course.id"
                        type="button"
                        class="course-group-detail__picker-card"
                        :class="{ 'is-selected': isCourseSelected(course.id) }"
                        @click="toggleCourseSelection(course.id)"
                    >
                        <span
                            class="course-group-detail__picker-thumb"
                            :class="{ 'no-image': !course.imageUrl }"
                            :style="pickerThumbStyle(course)"
                            aria-hidden="true"
                        />
                        <span class="course-group-detail__picker-body">
                            <strong class="course-group-detail__picker-title">{{ course.title }}</strong>
                            <span v-if="course.description" class="course-group-detail__picker-desc">
                                {{ course.description }}
                            </span>
                            <span v-else class="course-group-detail__picker-desc is-muted">
                                Sem descrição
                            </span>
                        </span>
                        <span class="course-group-detail__picker-check" aria-hidden="true">
                            <v-icon size="small">
                                {{ isCourseSelected(course.id) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                        </span>
                    </button>
                    <div
                        v-if="pickerHasMore"
                        class="course-group-detail__picker-more"
                    >
                        Role para carregar mais cursos…
                    </div>
                </div>

                <div class="course-group-detail__dialog-actions">
                    <span class="course-group-detail__dialog-count">
                        {{ selectedCourseIds.length }}
                        {{ selectedCourseIds.length === 1 ? 'curso selecionado' : 'cursos selecionados' }}
                    </span>
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="addDialog = false">
                        Cancelar
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        :disabled="!selectedCourseIds.length"
                        @click="confirmAddCourse"
                    >
                        Adicionar
                    </v-btn>
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import { toast } from 'vue3-toastify';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import corporateCourseGroupsApi from '@/utils/corporate/courseGroups';
import schoolCourseGroupsApi from '@/utils/courseGroups';
import coursesApi, { unwrapCoursesList } from '@/utils/courses';
import { isAdminCorporateMode } from '@/utils/corporate/adminDashboardMode';
import { isCorporateMockEnabled } from '@/utils/corporate/environment';
import { getState } from '@/mocks/corporate/store';

const DEFAULT_THUMB = require('@/assets/Weflix/default-video-thumbnail.jpg');
const PICKER_PAGE_SIZE = 24;

export default {
    name: 'CourseGroupDetail',
    components: { TitlePage, Loader, draggable },
    data() {
        return {
            loading: true,
            error: false,
            saving: false,
            group: null,
            platformCourses: [],
            activePlatformCourses: [],
            addDialog: false,
            selectedCourseIds: [],
            courseSearch: '',
            pickerVisibleCount: PICKER_PAGE_SIZE,
            sortableRows: [],
            orderSnapshot: '',
        };
    },
    computed: {
        ...mapState(['token', 'adminDashboardMode']),
        isCorporate() {
            return isAdminCorporateMode();
        },
        groupsApi() {
            return this.isCorporate
                ? corporateCourseGroupsApi
                : schoolCourseGroupsApi;
        },
        isPath() {
            return this.groupMode === 'path';
        },
        groupMode() {
            return this.group?.mode === 'path' ? 'path' : 'group';
        },
        modeOptions() {
            return [
                { text: 'Livre (grupo)', value: 'group' },
                { text: 'Trilha', value: 'path' },
            ];
        },
        isActive() {
            return this.normalizeGroupStatus === 'active';
        },
        normalizeGroupStatus() {
            const value = String(this.group?.status ?? '').trim().toLowerCase();
            if (value === 'active' || value === '1' || value === 'a') return 'active';
            if (value === 'internal' || value === '2' || value === 'internal_only' || value === 'internal-only') {
                return 'internal';
            }
            return 'inactive';
        },
        statusChipClass() {
            if (this.normalizeGroupStatus === 'active') return 'is-active';
            if (this.normalizeGroupStatus === 'internal') return 'is-internal';
            return 'is-inactive';
        },
        statusChipLabel() {
            if (this.normalizeGroupStatus === 'active') return this.$t('common.active');
            if (this.normalizeGroupStatus === 'internal') return this.$t('admin.courseForm.statusInternalOnly');
            return this.$t('common.inactive');
        },
        groupDescription() {
            return this.group?.shortDescription || this.group?.description || '';
        },
        coverUrl() {
            return this.group?.imageFinalPath || this.group?.imageUrl || null;
        },
        heroStyle() {
            if (!this.coverUrl) return {};
            return {
                backgroundImage: `url('${this.coverUrl}')`,
            };
        },
        linkedCourseIds() {
            if (!this.group) return [];
            if (Array.isArray(this.group.items) && this.group.items.length) {
                return this.group.items
                    .map((item) => (item.courseId != null ? String(item.courseId) : null))
                    .filter(Boolean);
            }
            return (this.group.courseIds || []).map(String);
        },
        availableCourses() {
            const linked = new Set(this.linkedCourseIds);
            return this.activePlatformCourses
                .filter((c) => !linked.has(String(c.id)))
                .map((c) => {
                    const title = c.comercialName || c.commercialName || c.name || `Curso ${c.id}`;
                    const description = this.descriptionPreview(c, 160);
                    return {
                        id: String(c.id),
                        title,
                        description,
                        imageUrl: c.imageFinalPath2 || c.imageFinalPath || c.imageUrl || null,
                    };
                });
        },
        filteredAvailableCourses() {
            const term = String(this.courseSearch || '').trim().toLowerCase();
            if (!term) return this.availableCourses;
            return this.availableCourses.filter((course) =>
                course.title.toLowerCase().includes(term),
            );
        },
        visiblePickerCourses() {
            return this.filteredAvailableCourses.slice(0, this.pickerVisibleCount);
        },
        pickerHasMore() {
            return this.pickerVisibleCount < this.filteredAvailableCourses.length;
        },
        courseRows() {
            if (!this.group) return [];
            if (Array.isArray(this.group.items) && this.group.items.length) {
                return this.group.items.map((item) => this.buildRow(item.courseId, {
                    unlockRule: item.unlockRule || 'always',
                    courseName: item.courseName || item.comercialName,
                    courseStatus: item.courseStatus,
                    imageUrl: item.imageFinalPath2 || item.imageFinalPath || item.imageUrl || null,
                    description: this.descriptionPreview(item),
                }));
            }
            return (this.group.courseIds || []).map((courseId) => this.buildRow(courseId));
        },
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler() {
                this.load();
            },
        },
        adminDashboardMode() {
            this.load();
        },
        courseRows: {
            immediate: true,
            handler(rows) {
                this.sortableRows = (rows || []).map((row) => ({ ...row }));
                this.orderSnapshot = this.orderSignature(this.sortableRows);
            },
        },
        courseSearch() {
            this.resetPickerPagination();
        },
        addDialog(open) {
            if (open) this.resetPickerPagination();
        },
    },
    methods: {
        resetPickerPagination() {
            this.pickerVisibleCount = PICKER_PAGE_SIZE;
            this.$nextTick(() => {
                const el = this.$refs.pickerScroll;
                if (el) el.scrollTop = 0;
            });
        },
        loadMorePickerCourses() {
            if (!this.pickerHasMore) return;
            this.pickerVisibleCount = Math.min(
                this.pickerVisibleCount + PICKER_PAGE_SIZE,
                this.filteredAvailableCourses.length,
            );
        },
        onPickerScroll(event) {
            const el = event?.target;
            if (!el || !this.pickerHasMore) return;
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (remaining < 120) this.loadMorePickerCourses();
        },
        ...mapActions(['storeCurrentCollections']),
        orderSignature(rows = []) {
            return (rows || []).map((row) => String(row.courseId)).join('|');
        },
        buildRow(courseId, extras = {}) {
            const course = this.platformCourses.find(
                (c) => String(c.id) === String(courseId),
            );
            const active = extras.courseStatus
                ? extras.courseStatus === 'active' || String(extras.courseStatus) === '1'
                : this.isCourseActive(courseId);
            return {
                courseId: courseId != null ? String(courseId) : null,
                courseName: extras.courseName
                    || course?.comercialName
                    || course?.commercialName
                    || course?.name
                    || `Curso ${courseId}`,
                description: extras.description || this.descriptionPreview(course),
                imageUrl: extras.imageUrl
                    || course?.imageFinalPath2
                    || course?.imageFinalPath
                    || course?.imageUrl
                    || null,
                moduleQty: Number(course?.moduleQty) || 0,
                lessonQty: Number(course?.lessonQty) || 0,
                active,
                unlockRule: extras.unlockRule || 'always',
            };
        },
        descriptionPreview(course, maxLength = 90) {
            if (!course) return '';
            const raw = course.shortDescription || course.description || course.longDescription || '';
            const text = String(raw).replace(/<[^>]+>/g, '').trim();
            if (!text) return '';
            return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
        },
        thumbStyle(row) {
            const url = row.imageUrl || DEFAULT_THUMB;
            return {
                backgroundImage: `url('${url}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            };
        },
        pickerThumbStyle(course) {
            return this.thumbStyle({ imageUrl: course.imageUrl });
        },
        isCourseSelected(courseId) {
            return this.selectedCourseIds.includes(String(courseId));
        },
        toggleCourseSelection(courseId) {
            const id = String(courseId);
            if (this.isCourseSelected(id)) {
                this.selectedCourseIds = this.selectedCourseIds.filter((item) => item !== id);
                return;
            }
            this.selectedCourseIds = [...this.selectedCourseIds, id];
        },
        resolveCourseName(courseId) {
            const course = this.platformCourses.find(
                (c) => String(c.id) === String(courseId),
            );
            return course?.name || course?.comercialName || course?.commercialName || null;
        },
        isActiveCourseStatus(status) {
            const normalized = String(status ?? '').trim().toLowerCase();
            return normalized === '1'
                || normalized === '2'
                || normalized === 'active'
                || normalized === 'internal'
                || normalized === 'a';
        },
        isCourseActive(courseId) {
            const course = this.platformCourses.find(
                (c) => String(c.id) === String(courseId),
            );
            if (!course) return false;
            return this.isActiveCourseStatus(course.status);
        },
        applyCourseCatalog(allCourses = [], activeCourses = null) {
            const list = Array.isArray(allCourses) ? allCourses : [];
            this.platformCourses = list;
            this.activePlatformCourses = Array.isArray(activeCourses)
                ? activeCourses
                : list.filter((course) => this.isActiveCourseStatus(course?.status));
        },
        async loadPlatformCourses() {
            if (this.isCorporate && isCorporateMockEnabled()) {
                this.applyCourseCatalog(getState().courses || []);
                return;
            }
            try {
                const allResponse = await coursesApi.getDataCourse(this.token);
                const list = unwrapCoursesList(allResponse);
                this.applyCourseCatalog(
                    list,
                    list.filter((course) => this.isActiveCourseStatus(course?.status)),
                );
            } catch (e) {
                if (this.isCorporate && isCorporateMockEnabled()) {
                    this.applyCourseCatalog(getState().courses || []);
                    return;
                }
                this.applyCourseCatalog([]);
            }
        },
        async load() {
            const id = this.$route.params.id;
            if (!id) {
                this.error = true;
                this.loading = false;
                return;
            }
            this.loading = true;
            this.error = false;
            try {
                await this.loadPlatformCourses();
                this.group = await this.groupsApi.getById(id, this.token);
            } catch (e) {
                this.error = true;
                this.group = null;
            } finally {
                this.loading = false;
            }
        },
        goBack() {
            this.$router.push('/dashboard/cursos');
        },
        editGroup() {
            const id = this.$route.params.id;
            this.$router.push({
                path: '/dashboard/form-curso',
                query: { kind: 'group', id: String(id) },
            });
        },
        viewCourse(courseId) {
            if (!courseId) return;
            const course = this.platformCourses.find(
                (c) => String(c.id) === String(courseId),
            );
            if (course) {
                this.storeCurrentCollections({ currentCollections: course });
            }
            this.$router.push('/dashboard/curso');
        },
        openAddDialog() {
            this.selectedCourseIds = [];
            this.courseSearch = '';
            this.resetPickerPagination();
            this.addDialog = true;
        },
        itemsFromRows(rows = this.sortableRows) {
            return (rows || []).map((row, index) => ({
                courseId: String(row.courseId),
                order: index + 1,
                unlockRule: this.isPath && index > 0 ? 'previous_completed' : 'always',
            }));
        },
        buildUpdatePayload(items) {
            const normalized = items.map((item, index) => ({
                courseId: String(item.courseId),
                order: index + 1,
                unlockRule: this.isPath && index > 0
                    ? 'previous_completed'
                    : (item.unlockRule || 'always'),
            }));
            return {
                ...this.group,
                items: normalized,
                courseIds: normalized.map((item) => item.courseId),
                target: this.group.target || { sectorIds: [], roleIds: [] },
            };
        },
        async persistItems(items, { successMessage } = {}) {
            if (!this.group?.id) return false;
            this.saving = true;
            try {
                const payload = this.buildUpdatePayload(items);
                this.group = await this.groupsApi.update(this.group.id, payload, this.token);
                if (successMessage) {
                    toast.success(successMessage);
                }
                this.orderSnapshot = this.orderSignature(this.sortableRows);
                return true;
            } catch (e) {
                toast.error(this.$t('admin.courseGroupDetail.updateCoursesError'));
                await this.load();
                return false;
            } finally {
                this.saving = false;
            }
        },
        async confirmAddCourse() {
            if (!this.selectedCourseIds.length) return;
            const items = this.itemsFromRows();
            const existing = new Set(items.map((item) => String(item.courseId)));
            this.selectedCourseIds.forEach((courseId) => {
                const id = String(courseId);
                if (existing.has(id)) return;
                items.push({
                    courseId: id,
                    order: items.length + 1,
                    unlockRule: this.isPath && items.length ? 'previous_completed' : 'always',
                });
                existing.add(id);
            });
            await this.persistItems(items, {
                successMessage: 'Cursos adicionados ao grupo.',
            });
            this.addDialog = false;
            this.selectedCourseIds = [];
            this.courseSearch = '';
        },
        async onDragEnd() {
            const nextSignature = this.orderSignature(this.sortableRows);
            if (nextSignature === this.orderSnapshot) return;
            await this.persistItems(this.itemsFromRows(), {
                successMessage: 'Ordem dos cursos atualizada.',
            });
        },
        async onModeChange(mode) {
            if (!this.group?.id || this.saving) return;
            const nextMode = mode === 'path' ? 'path' : 'group';
            if (nextMode === this.groupMode) return;

            this.saving = true;
            try {
                const items = this.itemsFromRows().map((item, index) => ({
                    courseId: String(item.courseId),
                    order: index + 1,
                    unlockRule: nextMode === 'path' && index > 0
                        ? 'previous_completed'
                        : 'always',
                }));
                const payload = {
                    ...this.group,
                    mode: nextMode,
                    items,
                    courseIds: items.map((item) => item.courseId),
                    target: this.group.target || { sectorIds: [], roleIds: [] },
                };
                this.group = await this.groupsApi.update(this.group.id, payload, this.token);
                toast.success(
                    nextMode === 'path'
                        ? 'Modelo atualizado para Trilha.'
                        : 'Modelo atualizado para Livre (grupo).',
                );
            } catch (e) {
                toast.error(this.$t('admin.courseGroupDetail.updateModelError'));
                await this.load();
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.course-group-detail__placeholder {
    padding: 2.5rem 1.5rem;
    text-align: center;
    color: #6b7280;
}

.course-group-detail__hero {
    position: relative;
    min-height: 220px;
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 1.25rem;
    background: linear-gradient(145deg, #eef1fb 0%, #e3e8f8 100%);
    background-size: cover;
    background-position: center;
}

.course-group-detail__hero.has-image {
    background-color: #1a1d26;
}

.course-group-detail__hero-overlay {
    position: relative;
    z-index: 1;
    min-height: 220px;
    padding: 1.5rem 1.6rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.45rem;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.15) 0%,
        rgba(15, 23, 42, 0.72) 100%
    );
    color: #fff;
}

.course-group-detail__hero:not(.has-image) .course-group-detail__hero-overlay {
    background: transparent;
    color: #1a1d26;
}

.course-group-detail__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 0.25rem;
}

.course-group-detail__chip {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
}

.course-group-detail__chip.is-group {
    background: #3b3f8c;
    color: #fff;
}

.course-group-detail__chip.is-path {
    background: #1f7a57;
    color: #fff;
}

.course-group-detail__chip.is-active {
    background: rgba(34, 197, 94, 0.2);
    color: #15803d;
}

.course-group-detail__chip.is-inactive {
    background: rgba(107, 114, 128, 0.2);
    color: #4b5563;
}

.course-group-detail__chip.is-internal {
    background: rgba(21, 101, 192, 0.18);
    color: #1565c0;
}

.course-group-detail__hero.has-image .course-group-detail__chip.is-active {
    background: rgba(34, 197, 94, 0.35);
    color: #dcfce7;
}

.course-group-detail__hero.has-image .course-group-detail__chip.is-inactive {
    background: rgba(255, 255, 255, 0.2);
    color: #f3f4f6;
}

.course-group-detail__hero.has-image .course-group-detail__chip.is-internal {
    background: rgba(21, 101, 192, 0.4);
    color: #dbeafe;
}

.course-group-detail__hero-title {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
}

.course-group-detail__hero-desc {
    margin: 0;
    max-width: 52rem;
    font-size: 0.95rem;
    line-height: 1.45;
    opacity: 0.92;
}

.course-group-detail__hero-meta {
    margin: 0.15rem 0 0;
    font-size: 0.85rem;
    font-weight: 600;
    opacity: 0.85;
}

.course-group-detail__panel {
    padding-top: 1.25rem;
}

.course-group-detail__section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.course-group-detail__section-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-shrink: 0;
}

.course-group-detail__mode-select {
    width: 180px;
    min-width: 160px;
}

.course-group-detail__section-head h3 {
    margin: 0 0 0.25rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1a1d26;
}

.course-group-detail__section-head p {
    margin: 0;
    font-size: 0.85rem;
    color: #6b7280;
}

.course-group-detail__empty {
    padding: 1.5rem;
    text-align: center;
    color: #6b7280;
    border: 1px dashed #e2e5ec;
    border-radius: 12px;
}

.course-group-detail__list {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.course-group-detail__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 0.9rem;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    background: #fff;
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.course-group-detail__row:hover {
    background: #fafbff;
    border-color: #dfe3ee;
    box-shadow: 0 1px 2px rgba(26, 29, 38, 0.04);
}

.course-group-detail__row-main {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    flex: 1;
}

.course-group-detail__order {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 8px;
    background: #eef0fc;
    color: #250891;
    font-size: 0.8rem;
    font-weight: 700;
    flex-shrink: 0;
}

.course-group-detail__thumb {
    width: 72px;
    height: 72px;
    min-width: 72px;
    border: 0;
    border-radius: 12px;
    background-color: #eef0f3;
    background-size: cover;
    background-position: center;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
}

.course-group-detail__meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
}

.course-group-detail__title-btn {
    border: 0;
    background: transparent;
    padding: 0;
    text-align: left;
    cursor: pointer;
}

.course-group-detail__title {
    display: block;
    font-size: 0.98rem;
    font-weight: 700;
    color: #1a1d26;
    line-height: 1.3;
}

.course-group-detail__desc {
    margin: 0;
    font-size: 0.8rem;
    color: #6b7280;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.course-group-detail__stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.45rem;
    margin-top: 0.15rem;
    font-size: 0.78rem;
    color: #5c6370;
}

.course-group-detail__status {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 700;
}

.course-group-detail__status.is-active {
    background: rgba(34, 197, 94, 0.14);
    color: #15803d;
}

.course-group-detail__status.is-inactive {
    background: rgba(107, 114, 128, 0.14);
    color: #4b5563;
}

.course-group-detail__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.55rem;
    flex-shrink: 0;
}

.course-group-detail__drag-handle {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #8b929e;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    flex-shrink: 0;
    padding: 0;
}

.course-group-detail__drag-handle:active {
    cursor: grabbing;
}

.course-group-detail__drag-handle:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.course-group-detail__ghost {
    opacity: 0.55;
}

.course-group-detail__dialog {
    padding: 1.25rem 1.35rem 1.1rem;
    max-height: min(82vh, 760px);
    display: flex;
    flex-direction: column;
}

.course-group-detail__dialog-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(220px, 280px);
    gap: 1rem;
    align-items: end;
    margin-bottom: 1rem;
}

.course-group-detail__dialog h3 {
    margin: 0 0 0.35rem;
    font-size: 1.1rem;
    font-weight: 700;
}

.course-group-detail__dialog-head p {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
}

.course-group-detail__dialog-search {
    width: 100%;
}

.course-group-detail__dialog-empty {
    padding: 2rem 1rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.9rem;
}

.course-group-detail__picker-grid {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    overflow: auto;
    flex: 1 1 auto;
    min-height: 280px;
    max-height: min(52vh, 480px);
    padding: 0.15rem 0.2rem 0.35rem 0.05rem;
}

.course-group-detail__picker-card {
    position: relative;
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr) 28px;
    align-items: center;
    gap: 0.75rem;
    text-align: left;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
    cursor: pointer;
    padding: 0.55rem 0.65rem;
    min-height: 88px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.15s ease;
}

.course-group-detail__picker-card:hover {
    border-color: rgba(37, 8, 145, 0.35);
    box-shadow: 0 4px 12px rgba(37, 8, 145, 0.08);
    background: #fafbff;
}

.course-group-detail__picker-card.is-selected {
    border-color: #250891;
    box-shadow: 0 0 0 2px rgba(37, 8, 145, 0.18);
    background: #f5f3ff;
}

.course-group-detail__picker-thumb {
    display: block;
    width: 96px;
    height: 64px;
    flex-shrink: 0;
    border-radius: 8px;
    background: #eef0f5 center / cover no-repeat;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.course-group-detail__picker-thumb.no-image {
    background-color: #eef0f5;
}

.course-group-detail__picker-body {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    padding: 0;
}

.course-group-detail__picker-title {
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.3;
    color: #1a1d26;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.course-group-detail__picker-desc {
    font-size: 0.78rem;
    line-height: 1.4;
    color: #5c6370;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.course-group-detail__picker-desc.is-muted {
    color: #9aa1ad;
}

.course-group-detail__picker-check {
    position: static;
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background: #f3f4f8;
    color: #250891;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.course-group-detail__picker-card.is-selected .course-group-detail__picker-check {
    background: rgba(37, 8, 145, 0.12);
}

.course-group-detail__picker-more {
    text-align: center;
    font-size: 0.78rem;
    color: #8b929e;
    padding: 0.35rem 0 0.15rem;
}

.course-group-detail__dialog-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 0.85rem;
    border-top: 1px solid #eef0f4;
}

.course-group-detail__dialog-count {
    font-size: 0.82rem;
    color: #5c6370;
    font-weight: 600;
}

@media (max-width: 900px) {
    .course-group-detail__dialog-head {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 560px) {
    .course-group-detail__picker-card {
        grid-template-columns: 72px minmax(0, 1fr) 24px;
        gap: 0.55rem;
        padding: 0.45rem 0.5rem;
    }

    .course-group-detail__picker-thumb {
        width: 72px;
        height: 52px;
    }
}

.dashboard.dark .course-group-detail__section-head h3,
.dashboard.dark .course-group-detail__title {
    color: #f1f3f7;
}

.dashboard.dark .course-group-detail__row {
    background: #2a2d3a;
    border-color: #353945;
}

.dashboard.dark .course-group-detail__row:hover {
    background: #303442;
    border-color: #424654;
}

.dashboard.dark .course-group-detail__order {
    background: rgba(196, 181, 253, 0.15);
    color: #c4b5fd;
}

:global(.dashboard.dark) .course-group-detail__picker-card,
:global(.dark) .course-group-detail__picker-card {
    background: #252730;
    border-color: #353945;
}

:global(.dashboard.dark) .course-group-detail__picker-card:hover,
:global(.dark) .course-group-detail__picker-card:hover {
    background: #2a2e3a;
    border-color: #454a5a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

:global(.dashboard.dark) .course-group-detail__picker-card.is-selected,
:global(.dark) .course-group-detail__picker-card.is-selected {
    border-color: #a5b4fc;
    background: #2a2e3a;
}

:global(.dashboard.dark) .course-group-detail__picker-title,
:global(.dark) .course-group-detail__picker-title {
    color: #f1f3f7;
}

:global(.dashboard.dark) .course-group-detail__picker-desc,
:global(.dark) .course-group-detail__picker-desc {
    color: #9ca3b4;
}

:global(.dashboard.dark) .course-group-detail__picker-check,
:global(.dark) .course-group-detail__picker-check {
    background: #2a2e3a;
    color: #c4b5fd;
}

:global(.dashboard.dark) .course-group-detail__picker-more,
:global(.dark) .course-group-detail__picker-more,
:global(.dashboard.dark) .course-group-detail__dialog-count,
:global(.dark) .course-group-detail__dialog-count,
:global(.dashboard.dark) .course-group-detail__dialog-head p,
:global(.dark) .course-group-detail__dialog-head p {
    color: #9ca3b4;
}

@media (max-width: 960px) {
    .course-group-detail__row {
        flex-direction: column;
        align-items: stretch;
    }

    .course-group-detail__toolbar {
        justify-content: flex-end;
        flex-wrap: wrap;
    }
}

@media (max-width: 720px) {
    .course-group-detail__section-head {
        flex-direction: column;
        align-items: stretch;
    }

    .course-group-detail__section-actions {
        flex-wrap: wrap;
    }

    .course-group-detail__mode-select {
        width: 100%;
        min-width: 0;
        flex: 1 1 180px;
    }
}
</style>
